(() => {
  if (customElements.get('responsive-media-hero')) return;

  class ResponsiveMediaHero extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.slides = Array.from(this.querySelectorAll('[data-media-slide]'));
      if (!this.slides.length) return;
      this.index = 0;
      this.soundEnabled = false;
      this.mobileQuery = matchMedia('(max-width: 768px)');
      this.megawideQuery = matchMedia('(min-width: 1776px)');
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.setupViewportFit();
      this.connection = navigator.connection;
      this.autorotate = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.rotationPaused = this.motionQuery.matches || Boolean(this.connection?.saveData);
      this.visible = this.isInViewport();
      this.states = this.slides.map((slide) => {
        let sources = {};
        try {
          sources = JSON.parse(slide.querySelector('[data-video-sources]')?.textContent || '{}');
        } catch (_) {
          // A missing or unfinished video setting still leaves a usable poster.
        }
        return {
          slide,
          video: slide.querySelector('video'),
          control: slide.querySelector('[data-video-toggle]'),
          audioControl: slide.querySelector('[data-audio-toggle]'),
          wordControl: slide.querySelector('[data-word-toggle]'),
          words: slide.dataset.headlineMode === 'rotating' ? Array.from(slide.querySelectorAll('[data-rotating-word]')) : [],
          wordIndex: 0,
          wordsPausedByUser: false,
          audioLoaded: false,
          sources,
          generation: 0,
          pausedByUser: false,
          explicitPlay: false,
          blocked: false,
          failed: false,
          ended: false,
          introElapsed: 0
        };
      });
      this.states.forEach((state) => {
        state.words.forEach((word, index) => word.classList.toggle('is-current', index === 0));
        this.bindVideo(state);
      });
      this.querySelector('[data-media-controls]')?.removeAttribute('hidden');
      this.querySelector('[data-rotation-toggle]')?.toggleAttribute('hidden', !this.autorotate);
      this.listen(this, 'click', (event) => {
        if (performance.now() < (this.suppressClickUntil || 0)) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        this.onClick(event);
      }, { capture: true });
      this.listen(this, 'keydown', (event) => this.onKeydown(event));
      this.listen(this, 'pointerdown', (event) => {
        if (event.isPrimary && !event.target.closest('a:not([data-slide-link]), button, input, textarea, select')) {
          this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
        }
      });
      this.listen(this, 'pointerup', (event) => {
        const start = this.pointerStart;
        this.pointerStart = null;
        if (!start || event.pointerId !== start.id) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.suppressClickUntil = performance.now() + 400;
          this.select(this.index + (dx < 0 ? 1 : -1), true);
        }
      });
      this.listen(this, 'pointercancel', () => { this.pointerStart = null; });
      this.listen(this, 'mouseenter', () => { this.hovered = true; this.schedule(); });
      this.listen(this, 'mouseleave', () => { this.hovered = false; this.schedule(); });
      this.listen(this, 'focusin', () => { this.focused = true; this.schedule(); });
      this.listen(this, 'focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.schedule();
      });
      this.listen(document, 'visibilitychange', () => this.reconcile());
      [this.mobileQuery, this.megawideQuery].forEach((query) => this.listen(query, 'change', () => this.reconcile()));
      this.listen(this.motionQuery, 'change', () => {
        if (this.motionQuery.matches) this.rotationPaused = true;
        this.reconcile();
      });
      if (this.connection) this.listen(this.connection, 'change', () => {
        if (this.connection.saveData) this.rotationPaused = true;
        this.reconcile();
      });
      this.listen(document, 'shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
        if (index < 0) return;
        this.editorSelected = true;
        this.select(index);
      });
      this.listen(document, 'shopify:block:deselect', (event) => {
        if (!this.slides.some((slide) => slide.dataset.blockId === event.detail?.blockId)) return;
        this.editorSelected = false;
        this.reconcileWords(this.states[this.index]);
        this.schedule();
      });
      this.listen(document, 'shopify:section:unload', (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.disconnectedCallback();
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.reconcile();
        }, { threshold: [0, 0.05] });
        this.observer.observe(this);
      } else {
        const checkVisibility = () => {
          this.visible = this.isInViewport();
          this.reconcile();
        };
        this.listen(window, 'scroll', checkVisibility, { passive: true });
        this.listen(window, 'resize', checkVisibility, { passive: true });
      }
      this.select(0);
    }

    disconnectedCallback() {
      if (!this.abort) return;
      this.abort.abort();
      this.abort = null;
      this.observer?.disconnect();
      this.headerResizeObserver?.disconnect();
      clearTimeout(this.rotationTimer);
      this.states?.forEach((state) => {
        this.stopWords(state);
        this.release(state);
      });
    }

    listen(target, type, callback, options = {}) {
      target.addEventListener(type, callback, { ...options, signal: this.abort.signal });
    }

    setupViewportFit() {
      if (this.dataset.fitViewport !== 'true' && this.dataset.fitMobileViewport !== 'true') return;
      const lifecycle = this.abort;
      const updateOffset = () => {
        if (this.abort !== lifecycle) return;
        // Use document coordinates so scrolling cannot make the hero grow.
        const top = Math.max(0, this.getBoundingClientRect().top + (window.scrollY || 0));
        this.style.setProperty('--rmh-top-offset', `${Math.ceil(top)}px`);
      };
      updateOffset();
      this.listen(window, 'resize', updateOffset, { passive: true });
      if ('ResizeObserver' in window) {
        this.headerResizeObserver = new ResizeObserver(updateOffset);
        document.querySelectorAll('.shopify-section-group-header-group').forEach((header) => this.headerResizeObserver.observe(header));
      }
      document.fonts?.ready?.then(updateOffset);
    }

    isInViewport() {
      const rect = this.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
    }

    tier() {
      return this.mobileQuery.matches ? 'mobile' : this.megawideQuery.matches ? 'megawide' : 'desktop';
    }

    sourceFor(state) {
      const tier = this.tier();
      // A portrait upload is never used as the desktop or megawide video.
      const candidates = tier === 'mobile'
        ? state.sources.mobile?.length ? state.sources.mobile : state.sources.desktop
        : tier === 'megawide' && state.sources.megawide?.length ? state.sources.megawide : state.sources.desktop;
      const mp4s = (candidates || []).filter((source) => source.url && (source.mime_type === 'video/mp4' || source.format === 'mp4'));
      if (!mp4s.length) return null;
      const width = this.getBoundingClientRect().width;
      const target = Math.min(tier === 'mobile' ? 1080 : 1920, width * Math.min(devicePixelRatio || 1, 1.5));
      mp4s.sort((a, b) => Number(a.width || 0) - Number(b.width || 0));
      const source = mp4s.find((source) => Number(source.width) >= target) || mp4s[mp4s.length - 1];
      // Shopify can emit protocol-relative URLs, including on an HTTP dev preview.
      return source.url.startsWith('//') ? { ...source, url: `https:${source.url}` } : source;
    }

    prefersPoster(state) {
      return !state.explicitPlay && (this.motionQuery.matches || Boolean(this.connection?.saveData));
    }

    canRotate() {
      const state = this.states?.[this.index];
      return this.autorotate && !this.rotationPaused && !this.hovered && !this.focused && !this.editorSelected && this.visible && !document.hidden
        && !(state && this.hasWordAnimation(state) && state.wordsPausedByUser);
    }

    hasWordAnimation(state) {
      return state.slide.dataset.wordsEnabled === 'true' && state.words.length > 1;
    }

    hasWordControl(state) {
      return Boolean(state.wordControl && this.hasWordAnimation(state) && !this.motionQuery.matches);
    }

    canAnimateWords(state) {
      return Boolean(this.abort && this.hasWordAnimation(state) && !this.motionQuery.matches
        && this.states[this.index] === state && this.visible && !document.hidden && !this.editorSelected
        && !state.wordsPausedByUser && !state.pausedByUser && !state.slide.classList.contains('is-intro-hidden'));
    }

    stopWords(state) {
      clearTimeout(state.wordTimer);
      clearTimeout(state.wordTransitionTimer);
      state.wordTimer = null;
      state.wordTransitionTimer = null;
      state.words.forEach((word) => word.classList.remove('is-leaving'));
    }

    reconcileWords(state) {
      if (!this.canAnimateWords(state)) {
        this.stopWords(state);
        if (this.motionQuery.matches && state.wordIndex !== 0) {
          state.wordIndex = 0;
          state.words.forEach((word, index) => word.classList.toggle('is-current', index === 0));
        }
      } else if (!state.wordTimer) {
        const interval = Math.max(2, Math.min(12, Number(state.slide.dataset.wordInterval) || 3)) * 1000;
        state.wordTimer = setTimeout(() => {
          state.wordTimer = null;
          if (!this.canAnimateWords(state)) {
            this.stopWords(state);
            return;
          }
          state.words[state.wordIndex].classList.remove('is-current');
          state.words[state.wordIndex].classList.add('is-leaving');
          state.wordIndex = (state.wordIndex + 1) % state.words.length;
          state.words[state.wordIndex].classList.add('is-current');
          clearTimeout(state.wordTransitionTimer);
          state.wordTransitionTimer = setTimeout(() => {
            state.wordTransitionTimer = null;
            state.words.forEach((word) => word.classList.remove('is-leaving'));
          }, 320);
          this.reconcileWords(state);
        }, interval);
      }
      this.updateWordControl(state);
    }

    updateWordControl(state) {
      if (!state.wordControl) return;
      state.wordControl.hidden = !this.hasWordControl(state);
      const paused = state.wordsPausedByUser || state.pausedByUser;
      const label = paused ? state.wordControl.dataset.resumeLabel : state.wordControl.dataset.pauseLabel;
      state.wordControl.setAttribute('aria-label', label);
      state.wordControl.setAttribute('title', label);
      state.wordControl.setAttribute('aria-pressed', String(paused));
      state.wordControl.classList.toggle('is-paused', paused);
    }

    toggleWords(state) {
      if (!this.hasWordControl(state)) return;
      if (!state.wordsPausedByUser && !state.pausedByUser) {
        state.wordsPausedByUser = true;
        state.pausedByUser = true;
        state.video?.pause();
        state.slide.classList.remove('is-intro-hidden');
        this.stopWords(state);
        this.updateControl(state);
        this.schedule();
      } else {
        state.wordsPausedByUser = false;
        state.pausedByUser = false;
        state.explicitPlay = true;
        state.blocked = false;
        state.failed = false;
        if (state.ended && state.video) {
          state.video.currentTime = 0;
          state.ended = false;
          state.introElapsed = 0;
        }
        this.reconcile();
      }
    }

    select(index, announce = false) {
      const next = (index + this.slides.length) % this.slides.length;
      if (next !== this.index && this.states[this.index]?.slide.contains(document.activeElement)) {
        // Never hide the keyboard user's focused link or video control.
        this.focus({ preventScroll: true });
      }
      this.states.forEach((state, i) => {
        const active = i === next;
        if (!active) this.stopWords(state);
        if (!active || i !== this.index) this.release(state);
        state.slide.hidden = !active;
        state.slide.inert = !active;
        state.slide.setAttribute('aria-hidden', String(!active));
        state.slide.classList.toggle('is-active', active);
        if (!active) state.slide.classList.remove('is-intro-hidden');
      });
      this.index = next;
      this.querySelectorAll('[data-slide-dot]').forEach((dot, i) => dot.setAttribute('aria-current', String(i === next)));
      if (announce) {
        const status = this.querySelector('[data-slide-status]');
        if (status) status.textContent = `Slide ${next + 1} of ${this.slides.length}`;
      }
      this.reconcile();
    }

    reconcile() {
      if (!this.abort || !this.states?.length) return;
      const state = this.states[this.index];
      const source = this.sourceFor(state);
      const slideLink = state.slide.querySelector('[data-slide-link]');
      if (slideLink) {
        const hasVisibleButton = Array.from(state.slide.querySelectorAll('.rmh__button')).some((button) => button.href === slideLink.href && button.getClientRects().length);
        // Preserve the CTA, but leave the video background available for touch pause.
        // Static desktop fallbacks and ordinary image slides retain their full-slide link.
        slideLink.hidden = this.dataset.videoControls === 'minimal' && Boolean(source) && hasVisibleButton;
        slideLink.tabIndex = hasVisibleButton ? -1 : 0;
        if (hasVisibleButton) slideLink.setAttribute('aria-hidden', 'true');
        else slideLink.removeAttribute('aria-hidden');
      }
      if (!state.video || !source || !this.visible || document.hidden || this.prefersPoster(state)) {
        this.release(state);
      } else {
        if (state.url && state.url !== source.url) this.release(state);
        if (!state.pausedByUser && !state.blocked && !state.failed && !state.ended) {
          this.play(state, source);
        }
      }
      this.updateControl(state);
      this.reconcileWords(state);
      this.schedule();
    }

    play(state, source) {
      if (state.pending || (!state.video.paused && state.url === source.url)) return;
      const video = state.video;
      if (!state.url) {
        state.generation += 1;
        state.url = source.url;
        video.defaultMuted = true;
        video.muted = !this.soundEnabled;
        video.loop = state.slide.dataset.videoLoop === 'true' && !(this.autorotate && state.slide.dataset.videoAdvance === 'ended');
        video.src = source.url;
        video.load();
      }
      const generation = state.generation;
      state.pending = true;
      this.watchPlayback(state);
      const playback = video.play();
      Promise.resolve(playback).then(() => {
        if (generation !== state.generation) return;
        state.pending = false;
        if (!this.abort || this.states[this.index] !== state || !this.visible || document.hidden) this.release(state);
      }).catch(() => {
        if (generation !== state.generation) return;
        state.blocked = true;
        this.release(state);
        this.updateControl(state);
        this.reconcileWords(state);
        this.schedule();
      });
    }

    release(state) {
      state.generation += 1;
      state.pending = false;
      state.audioLoaded = false;
      clearTimeout(state.watchdog);
      this.pauseIntro(state);
      state.introElapsed = 0;
      state.slide.classList.remove('is-video-ready', 'is-video-playing', 'is-intro-hidden');
      if (state.video?.hasAttribute('src')) {
        state.video.pause();
        state.video.removeAttribute('src');
        state.video.load();
      }
      state.url = null;
      this.updateAudioControl(state);
      // Offscreen and inactive videos can restart, but browser rejections stay blocked until Play.
      state.ended = false;
    }

    bindVideo(state) {
      if (!state.video) return;
      this.updateAudioControl(state);
      this.listen(state.video, 'loadedmetadata', () => {
        if (this.states[this.index] !== state || !state.url) return;
        state.audioLoaded = true;
        this.updateAudioControl(state);
      });
      this.listen(state.video, 'volumechange', () => {
        if (this.states[this.index] !== state || !state.url || !state.video.hasAttribute('src')) return;
        this.soundEnabled = !state.video.muted;
        this.states.forEach((item) => this.updateAudioControl(item));
      });
      this.listen(state.video, 'playing', () => {
        if (this.states[this.index] !== state || !this.visible || document.hidden || !state.url) {
          this.release(state);
          return;
        }
        clearTimeout(state.watchdog);
        state.audioLoaded = true;
        state.slide.classList.add('is-video-ready', 'is-video-playing');
        state.ended = false;
        this.startIntro(state);
        this.updateControl(state);
        this.schedule();
      });
      this.listen(state.video, 'waiting', () => {
        if (!state.url) return;
        this.pauseIntro(state);
        this.watchPlayback(state);
      });
      this.listen(state.video, 'pause', () => {
        clearTimeout(state.watchdog);
        state.slide.classList.remove('is-video-playing');
        this.pauseIntro(state);
        this.updateControl(state);
      });
      this.listen(state.video, 'ended', () => {
        state.ended = true;
        this.pauseIntro(state);
        state.slide.classList.remove('is-video-playing', 'is-intro-hidden');
        this.updateControl(state);
        if (this.states[this.index] === state && this.canRotate() && state.slide.dataset.videoAdvance === 'ended') this.select(this.index + 1);
        else this.schedule();
      });
      this.listen(state.video, 'error', () => {
        if (!state.url) return;
        state.failed = true;
        this.release(state);
        this.updateControl(state);
        this.reconcileWords(state);
        this.schedule();
      });
    }

    watchPlayback(state) {
      clearTimeout(state.watchdog);
      state.watchdog = setTimeout(() => {
        if (!state.url || this.states[this.index] !== state) return;
        state.failed = true;
        this.release(state);
        this.updateControl(state);
        this.reconcileWords(state);
        this.schedule();
      }, 12000);
    }

    startIntro(state) {
      if (state.slide.dataset.textMode !== 'intro' || state.introStarted) return;
      state.introStarted = performance.now();
      const remaining = Number(state.slide.dataset.introSeconds || 4) * 1000 - state.introElapsed;
      state.introTimer = setTimeout(() => {
        state.slide.classList.add('is-intro-hidden');
        this.reconcileWords(state);
      }, Math.max(0, remaining));
    }

    pauseIntro(state) {
      clearTimeout(state.introTimer);
      if (state.introStarted) state.introElapsed += performance.now() - state.introStarted;
      state.introStarted = 0;
    }

    updateControl(state) {
      this.updateAudioControl(state);
      this.updateWordControl(state);
      if (!state.control) return;
      const playRequired = !state.pausedByUser && Boolean(state.blocked || state.failed || state.ended || this.prefersPoster(state));
      state.control.hidden = !this.sourceFor(state) || (this.hasWordControl(state) && !playRequired);
      const playing = Boolean(state.url && !state.video?.paused && !state.ended);
      const label = playing ? 'Pause video' : state.ended ? 'Replay video' : 'Play video';
      state.control.setAttribute('aria-label', label);
      state.control.setAttribute('aria-pressed', String(playing));
      state.control.textContent = label;
      // A clean autoplay hero still needs an obvious way to retry or resume.
      state.control.classList.toggle('is-play-required', Boolean(state.blocked || state.failed || state.pausedByUser || state.ended || this.prefersPoster(state)));
    }

    updateAudioControl(state) {
      if (!state.audioControl) return;
      const attached = Boolean(state.url && state.video?.hasAttribute('src'));
      const soundOn = attached ? !state.video.muted : this.soundEnabled;
      const label = soundOn ? 'Turn sound off' : 'Turn sound on';
      state.audioControl.setAttribute('aria-label', label);
      state.audioControl.setAttribute('title', label);
      state.audioControl.setAttribute('aria-pressed', String(soundOn));
      state.audioControl.hidden = !attached || !state.audioLoaded || this.states[this.index] !== state
        || !this.visible || document.hidden || state.blocked || state.failed || this.prefersPoster(state);
    }

    toggleAudio(state) {
      if (!state.video || !state.url || !state.video.hasAttribute('src') || state.audioControl?.hidden) return;
      // Only a direct sound-button gesture changes audibility; never starts playback.
      this.soundEnabled = state.video.muted;
      state.video.muted = !this.soundEnabled;
      this.states.forEach((item) => this.updateAudioControl(item));
    }

    schedule() {
      clearTimeout(this.rotationTimer);
      const control = this.querySelector('[data-rotation-toggle]');
      if (control) {
        const label = this.rotationPaused ? 'Play slideshow' : 'Pause slideshow';
        control.textContent = label;
        control.setAttribute('aria-label', label);
        control.setAttribute('aria-pressed', String(!this.rotationPaused));
      }
      if (!this.canRotate()) return;
      const state = this.states[this.index];
      const source = this.sourceFor(state);
      const waitForEnd = source && state.slide.dataset.videoAdvance === 'ended' && !state.pausedByUser && !state.blocked && !state.failed && !this.prefersPoster(state);
      if (waitForEnd && !state.ended) return;
      const delay = state.ended && waitForEnd ? 500 : Number(state.slide.dataset.duration || this.dataset.speed || 7) * 1000;
      this.rotationTimer = setTimeout(() => this.select(this.index + 1), delay);
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button) {
        const state = this.states[this.index];
        if (this.dataset.videoControls === 'minimal'
          && event.target.closest('[data-media-slide]') === state.slide
          && !event.target.closest('a, button, input, textarea, select, label, summary, [contenteditable]')
          && state.video && this.sourceFor(state)) {
          this.toggleVideo(state);
        }
        return;
      }
      if (!this.contains(button)) return;
      if (button.hasAttribute('data-prev-slide')) this.select(this.index - 1, true);
      if (button.hasAttribute('data-next-slide')) this.select(this.index + 1, true);
      if (button.hasAttribute('data-slide-dot')) this.select(Number(button.dataset.slideDot), true);
      if (button.hasAttribute('data-rotation-toggle')) {
        this.rotationPaused = !this.rotationPaused;
        this.schedule();
      }
      if (button.hasAttribute('data-video-toggle')) {
        this.toggleVideo(this.states[this.index]);
      }
      if (button.hasAttribute('data-audio-toggle') && button === this.states[this.index].audioControl) {
        this.toggleAudio(this.states[this.index]);
      }
      if (button.hasAttribute('data-word-toggle') && button === this.states[this.index].wordControl) {
        this.toggleWords(this.states[this.index]);
      }
    }

    toggleVideo(state) {
      if (!state.video || !this.sourceFor(state)) return;
      if (state.url && !state.video.paused) {
        state.pausedByUser = true;
        state.wordsPausedByUser = true;
        state.video.pause();
        state.slide.classList.remove('is-intro-hidden');
        this.updateControl(state);
        this.reconcileWords(state);
        this.schedule();
      } else {
        state.pausedByUser = false;
        state.wordsPausedByUser = false;
        state.explicitPlay = true;
        state.blocked = false;
        state.failed = false;
        if (state.ended) {
          state.video.currentTime = 0;
          state.ended = false;
          state.introElapsed = 0;
        }
        this.reconcile();
      }
    }

    onKeydown(event) {
      if (this.slides.length < 2 || event.altKey || event.ctrlKey || event.metaKey || event.target.closest('input, textarea, select')) return;
      const indexes = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 };
      if (!(event.key in indexes)) return;
      event.preventDefault();
      this.select(indexes[event.key], true);
    }
  }

  customElements.define('responsive-media-hero', ResponsiveMediaHero);
})();
